/**
 * Navigation constants for the Raidu website
 * Centralizes all navigation links for use across components
 */

import {
  ShieldCheckIcon,
  CubeIcon,
  LockClosedIcon,
  ClipboardDocumentCheckIcon,
  ArrowPathIcon,
  HeartIcon,
  BanknotesIcon,
  ScaleIcon,
  BuildingLibraryIcon,
  AcademicCapIcon,
  InformationCircleIcon,
  BriefcaseIcon,
  PhoneIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline'
import { PlayCircleIcon } from '@heroicons/react/20/solid'

/**
 * Main navigation items for the header with flyout submenus and icons
 */
export const headerNavigation = [
  {
    name: "Why Raidu?",
    href: "/",
    submenu: [
      { name: "Cloud Agnostic", href: "/#cloud-agnostic", description: "Deploy Raidu in any cloud environment", icon: CubeIcon },
      { name: "Data Masking", href: "/#data-masking", description: "Protect sensitive data in AI interactions", icon: ShieldCheckIcon },
      { name: "Prompt Improvement", href: "/#prompt-improvement", description: "Enhance AI prompts for better results", icon: ClipboardDocumentCheckIcon },
      { name: "Private RAG", href: "/#private-rag", description: "Secure retrieval-augmented generation", icon: LockClosedIcon },
      { name: "Multi-LLM Support", href: "/#multi-llm-support", description: "Connect with all major AI platforms", icon: ArrowPathIcon },
    ],
  },
  {
    name: "Industries",
    href: "/industries",
    submenu: [
      { name: "Healthcare", href: "/industries/healthcare", description: "Secure AI for patient care and operations", icon: HeartIcon },
      { name: "Finance", href: "/industries/finance", description: "Compliant AI for financial institutions", icon: BanknotesIcon },
      { name: "Legal", href: "/industries/legal", description: "Protect sensitive legal AI applications", icon: ScaleIcon },
      { name: "Manufacturing", href: "/industries/manufacturing", description: "AI solutions for manufacturing processes", icon: CubeIcon },
      { name: "Education", href: "/industries/education", description: "Safe AI implementation for educational institutions", icon: AcademicCapIcon },
      { name: "Retail", href: "/industries/retail", description: "Personalized AI solutions for retail businesses", icon: ShoppingBagIcon },
    ],
  },
  {
    name: "Insights",
    href: "/blog",
    // submenu: [
    //   { name: "All Posts", href: "/blog", description: "Browse all blog articles", icon: NewspaperIcon },
    //   { name: "AI Governance", href: "/blog/category/ai-governance", description: "Insights on responsible AI adoption", icon: ClipboardDocumentCheckIcon },
    //   { name: "Security", href: "/blog/category/security", description: "Latest on AI security practices", icon: ShieldCheckIcon },
    //   { name: "Compliance", href: "/blog/category/compliance", description: "Updates on AI regulations and standards", icon: ScaleIcon },
    // ],
  },
  {
    name: "Company",
    href: "/company",
    submenu: [
      { name: "About Us", href: "/company/about", description: "Our story and values", icon: InformationCircleIcon },
      { name: "Careers", href: "/company/careers", description: "Join our growing team", icon: BriefcaseIcon },
      // { name: "Press", href: "/company/press", description: "News and media coverage", icon: NewspaperIcon },
      { name: "Contact", href: "/company/contact", description: "Get in touch with our team", icon: PhoneIcon },
    ],
  },
];

/**
 * Call-to-action items for the header dropdown menus
 */
export const headerCallsToAction = [
  { name: 'Book Demo', href: '/booking', icon: PlayCircleIcon },
  { name: 'Contact sales', href: '/company/contact', icon: PhoneIcon },
];

/**
 * Footer navigation links organized by section
 */
/**
 * Social media icon type
 */
export type SocialIcon = {
  name: string;
  href: string;
  iconName: string; // Reference to the icon name
};

/**
 * Footer navigation links organized by section
 */
export const footerNavigation = {
  whyRaidu: [
    { name: 'Cloud Agnostic', href: '/#cloud-agnostic', description: 'Deploy Raidu in any cloud environment' },
    { name: 'Data Masking', href: '/#data-masking', description: 'Protect sensitive data in AI interactions' },
    { name: 'Prompt Improvement', href: '/#prompt-improvement', description: 'Enhance AI prompts for better results' },
    { name: 'Private RAG', href: '/#private-rag', description: 'Secure retrieval-augmented generation' },
    { name: 'Multi-LLM Support', href: '/#multi-llm-support', description: 'Connect with all major AI platforms' },
  ],
  industries: [
    { name: 'Healthcare', href: '/industries/healthcare', description: 'Secure AI for patient care and operations' },
    { name: 'Finance', href: '/industries/finance', description: 'Compliant AI for financial institutions' },
    { name: 'Legal', href: '/industries/legal', description: 'Protect sensitive legal AI applications' },
    { name: 'Manufacturing', href: '/industries/manufacturing', description: 'AI solutions for manufacturing processes' },
    { name: 'Education', href: '/industries/education', description: 'Safe AI implementation for educational institutions' },
    { name: 'Retail', href: '/industries/retail', description: 'Personalized AI solutions for retail businesses' },
  ],
  company: [
    { name: 'About Us', href: '/company/about' },
    { name: 'Careers', href: '/company/careers' },
    { name: 'Contact', href: '/company/contact' },
  ],
  legal: [
    { name: 'Privacy Policy', href: '/privacy-policy' },
    { name: 'Terms of Service', href: '/terms-of-service' },
    { name: 'Cookie Policy', href: '/privacy-policy#cookie-policy' },
  ],
  social: [
    {
      name: 'LinkedIn',
      href: 'http://linkedin.com/company/raidu-ai',
      iconName: 'linkedin',
    },
    {
      name: 'X',
      href: 'https://x.com/RaiduInc',
      iconName: 'twitter',
    },
    {
      name: 'GitHub',
      href: 'https://github.com/Raidu-Inc',
      iconName: 'github',
    },
  ] as SocialIcon[],
};
